import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { Card, CardError, Skel, Tween } from './parts';
import { ago, ChatRow, ChatSection, Failed, failed, int } from './api';

/** An open bot chat with a message in the last 10 minutes counts as live. */
const LIVE_MS = 10 * 60_000;

type Status = { label: string; tone: 'good' | 'warn' | 'plain' | 'faint'; pulse: boolean };

const statusOf = (r: ChatRow, now: number): Status => {
  const recent = !!r.lastMessageAt && now - new Date(r.lastMessageAt).getTime() < LIVE_MS;
  if (r.status === 'waiting_for_admin') return { label: 'Waiting', tone: 'warn', pulse: true };
  if (r.status === 'admin_joined') return { label: 'With team', tone: 'good', pulse: recent };
  if (r.status === 'closed') return { label: 'Closed', tone: 'faint', pulse: false };
  return recent ? { label: 'Live', tone: 'good', pulse: true } : { label: 'Idle', tone: 'plain', pulse: false };
};

const toneVar = { good: 'var(--oa-good)', warn: 'var(--oa-warn)', plain: 'var(--oa-muted)', faint: 'var(--oa-faint)' };
const clock = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const who = { visitor: '', ai: 'Bot: ', admin: 'Team: ' } as Record<string, string>;

const Row: React.FC<{ r: ChatRow; i: number; now: number }> = ({ r, i, now }) => {
  const st = statusOf(r, now);
  const page = r.page && r.page !== '/' ? r.page : 'Home';
  return (
    <li style={{ ['--i' as string]: i } as React.CSSProperties}>
      <Link to={`/admin/livechat?s=${r.id}`} className="oa-chat-row">
        <span className={cn('oa-pulse', st.pulse && 'is-live')} style={{ ['--st' as string]: toneVar[st.tone] } as React.CSSProperties} aria-hidden="true" />
        <span className="min-w-0">
          <span className="flex min-w-0 items-baseline gap-2">
            <b className="truncate">{r.visitor || 'Visitor'}</b>
            <span className="truncate text-[11.5px] text-[var(--oa-faint)]">on {page}</span>
          </span>
          {/* The preview gives way to the chat's facts on hover. */}
          <span className="oa-swap">
            <span className="truncate">{r.lastMessage ? `${who[r.lastSender || ''] ?? ''}${r.lastMessage}` : 'No messages yet'}</span>
            <span className="truncate">
              {int(r.messages)} messages · started {clock(r.createdAt)}
              {r.handedOver ? ' · handed to team' : ''}
            </span>
          </span>
        </span>
        <span className="flex flex-col items-end gap-1">
          <span className="whitespace-nowrap text-[11.5px] text-[var(--oa-muted)]" title={r.lastMessageAt ? clock(r.lastMessageAt) : undefined}>
            {ago(r.lastMessageAt || r.createdAt, now)}
          </span>
          <span className="flex items-center gap-1.5">
            {/* Closed chats keep their old unread counts; only open ones need a nudge. */}
            {r.unread > 0 && r.status !== 'closed' ? <span className="oa-chip is-warn !px-1.5 !py-0 text-[10.5px]">{r.unread} new</span> : null}
            <span className="oa-chat-st" style={{ ['--st' as string]: toneVar[st.tone] } as React.CSSProperties}>
              {st.label}
            </span>
          </span>
        </span>
      </Link>
    </li>
  );
};

export const ChatListCard: React.FC<{ data: ChatSection | Failed | undefined; rangeDays: number }> = ({ data, rangeDays }) => {
  const link = (
    <Link className="oa-icon-btn is-sm is-bordered" to="/admin/livechat" aria-label="Open live chat" title="Open live chat">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </Link>
  );

  if (!data || failed(data)) {
    return (
      <Card i={3} area="chat" title="Live chats" actions={link}>
        {data && failed(data) ? (
          <CardError message={`Chats did not load: ${data.error}`} />
        ) : (
          <>
            <Skel className="h-4 w-[70%]" />
            {[0, 1, 2, 3, 4].map((i) => (
              <Skel key={i} className="h-[52px] w-full" />
            ))}
          </>
        )}
      </Card>
    );
  }

  const now = Date.now();
  const t = data.totals;
  const open = data.now.active + data.now.waiting + data.now.withTeam;
  const share = t.chats ? Math.round((t.team / t.chats) * 100) : 0;

  return (
    <Card
      i={3}
      area="chat"
      title="Live chats"
      actions={
        <>
          {data.now.waiting ? (
            <span className="oa-chip is-warn">{data.now.waiting} waiting</span>
          ) : (
            <span className={cn('oa-chip', data.online ? 'is-good' : 'is-plain')}>{data.online ? 'Team online' : 'Team offline'}</span>
          )}
          {link}
        </>
      }
    >
      <div className="oa-kv">
        <span title={`Chats with at least one visitor message in the last ${rangeDays} days`}>
          Chats <b><Tween value={t.chats} /></b>
        </span>
        <span title={`${int(t.team)} of ${int(t.chats)} were handed to a person`}>
          Needed a person <b>{share}%</b>
        </span>
        <span>
          Open now <b>{open}</b>
        </span>
        <span title={`${int(t.visitorMessages)} from visitors, ${int(t.aiMessages)} from the bot, ${int(t.adminMessages)} from the team`}>
          Messages <b><Tween value={t.visitorMessages + t.aiMessages + t.adminMessages} /></b>
        </span>
      </div>
      {data.latest.length === 0 ? (
        <p className="oa-cap flex flex-1 items-center justify-center py-8">No chats yet. New conversations appear here as they start.</p>
      ) : (
        <ul className="oa-chat-list">
          {data.latest.map((r, i) => (
            <Row key={r.id} r={r} i={i} now={now} />
          ))}
        </ul>
      )}
    </Card>
  );
};
